import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Paper, ButtonGroup } from '@mui/material';
import { Preview, Edit } from '@mui/icons-material';

interface Props {
  value: string;
  onChange: (html: string) => void;
}

const TextContentEditor: React.FC<Props> = ({ value, onChange }) => {
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');

  const insertTag = (open: string, close: string) => {
    const ta = document.getElementById('text-editor') as HTMLTextAreaElement;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.substring(start, end);
    const newVal = value.substring(0, start) + open + selected + close + value.substring(end);
    onChange(newVal);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + open.length, start + open.length + selected.length);
    }, 0);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2" color="text.secondary">Content (HTML supported)</Typography>
        <ButtonGroup size="small" variant="outlined">
          <Button onClick={() => setMode('edit')} variant={mode === 'edit' ? 'contained' : 'outlined'} startIcon={<Edit />}>Edit</Button>
          <Button onClick={() => setMode('preview')} variant={mode === 'preview' ? 'contained' : 'outlined'} startIcon={<Preview />}>Preview</Button>
        </ButtonGroup>
      </Box>

      {mode === 'edit' ? (
        <Box>
          <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
            {[
              { label: 'H2', open: '<h2>', close: '</h2>' },
              { label: 'H3', open: '<h3>', close: '</h3>' },
              { label: 'Bold', open: '<strong>', close: '</strong>' },
              { label: 'Italic', open: '<em>', close: '</em>' },
              { label: 'UL', open: '<ul>\n  <li>', close: '</li>\n</ul>' },
              { label: 'LI', open: '<li>', close: '</li>' },
              { label: 'Code', open: '<code>', close: '</code>' },
              { label: 'Pre', open: '<pre><code>', close: '</code></pre>' },
              { label: 'Link', open: '<a href="">', close: '</a>' },
            ].map(btn => (
              <Button
                key={btn.label}
                size="small"
                variant="outlined"
                sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11 }}
                onClick={() => insertTag(btn.open, btn.close)}
              >
                {btn.label}
              </Button>
            ))}
          </Box>
          <TextField
            id="text-editor"
            fullWidth
            multiline
            rows={16}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={`<h2>Module Title</h2>\n<p>Your content here...</p>\n<ul>\n  <li>Key point 1</li>\n</ul>`}
            sx={{ fontFamily: 'monospace', '& textarea': { fontFamily: 'monospace', fontSize: 13 } }}
          />
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ p: 3, minHeight: 300, maxHeight: 500, overflow: 'auto' }}>
          {value ? (
            <Box
              sx={{ '& h2': { mt: 0 }, '& ul': { pl: 3 }, '& pre': { bgcolor: '#f5f5f5', p: 2, borderRadius: 1, overflow: 'auto' }, '& code': { bgcolor: '#f5f5f5', px: 0.5, borderRadius: 0.5, fontFamily: 'monospace' } }}
              dangerouslySetInnerHTML={{ __html: value }}
            />
          ) : (
            <Typography color="text.disabled" fontStyle="italic">Nothing to preview yet. Add some content in Edit mode.</Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default TextContentEditor;
